



import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../services/axiosInstance";

/* =====================================================
   ASYNC THUNKS
===================================================== */

// 📅 Daily Attendance (Admin Dashboard)
export const fetchDailyAttendance = createAsyncThunk(
  "attendanceDashboard/fetchDaily",
  async (date, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/api/attendance/attendance-dashboard/daily?date=${date}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

// 📊 Weekly Trend
export const fetchWeeklyTrend = createAsyncThunk(
  "attendanceDashboard/fetchWeekly",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `/api/attendance/attendance-dashboard/weekly-trend?start=${startDate}&end=${endDate}`
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

// 📈 Monthly Report
export const fetchMonthlyReport = createAsyncThunk(
  "attendanceDashboard/fetchMonthly",
  async ({ month, year }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `/api/attendance/attendance-dashboard/monthly-report?month=${month}&year=${year}`
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

// 🧑‍💼 Admin manual mark
export const markAttendance = createAsyncThunk(
  "attendanceDashboard/markAttendance",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `/api/attendance/attendance-dashboard/mark`,
        payload
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

/* =====================================================
   SLICE
===================================================== */

const attendanceDashboardSlice = createSlice({
  name: "attendanceDashboard",

  initialState: {
    // DATA
    daily: {
      loading: false,
      date: null,
      summary: null,
      employees: [],
      error: null
    },

    weekly: {
      loading: false,
      data: [],
      error: null
    },

    monthly: {
      loading: false,
      data: null,
      error: null
    },

    // LIVE SOCKET DATA
    liveUpdates: [],
    socketConnected: false,

    // FILTERS
    filters: {
      department: "All",
      status: "All",
      search: ""
    }
  },

  reducers: {
    /* ======================
       LIVE SOCKET ACTIONS
    ====================== */

    addLiveUpdate: (state, action) => {
      state.liveUpdates.unshift(action.payload);
      if (state.liveUpdates.length > 20) {
        state.liveUpdates.pop();
      }
    },

    updateEmployeeStatus: (state, action) => {
      const {
        employeeId,
        status,
        checkIn,
        checkOut,
        workHours,
        overtimeHours
      } = action.payload;

      const emp = state.daily.employees.find(
        (e) => e._id === employeeId
      );

      if (emp) {
        if (status) emp.status = status;
        if (checkIn) emp.checkIn = checkIn;
        if (checkOut) emp.checkOut = checkOut;
        if (workHours !== undefined) emp.workHours = workHours;
        if (overtimeHours !== undefined)
          emp.overtimeHours = overtimeHours;
      }
    },

    setSocketConnected: (state, action) => {
      state.socketConnected = action.payload;
    },

    /* ======================
       FILTERS
    ====================== */

    setDepartmentFilter: (state, action) => {
      state.filters.department = action.payload;
    },

    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
    },

    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
    },

    clearLiveUpdates: (state) => {
      state.liveUpdates = [];
    }
  },

  extraReducers: (builder) => {
    builder

      /* ======================
         DAILY
      ====================== */
      .addCase(fetchDailyAttendance.pending, (state) => {
        state.daily.loading = true;
      })
      .addCase(fetchDailyAttendance.fulfilled, (state, action) => {
        state.daily.loading = false;
        state.daily.date = action.payload.date;
        state.daily.summary = action.payload.summary;
        state.daily.employees = action.payload.employees;
      })
      .addCase(fetchDailyAttendance.rejected, (state, action) => {
        state.daily.loading = false;
        state.daily.error = action.payload;
      })

      /* ======================
         WEEKLY
      ====================== */
      .addCase(fetchWeeklyTrend.pending, (state) => {
        state.weekly.loading = true;
      })
      .addCase(fetchWeeklyTrend.fulfilled, (state, action) => {
        state.weekly.loading = false;
        state.weekly.data = action.payload;
      })
      .addCase(fetchWeeklyTrend.rejected, (state, action) => {
        state.weekly.loading = false;
        state.weekly.error = action.payload;
      })

      /* ======================
         MONTHLY
      ====================== */
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.monthly.loading = true;
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.monthly.loading = false;
        state.monthly.data = action.payload;
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.monthly.loading = false;
        state.monthly.error = action.payload;
      });
  }
});

/* =====================================================
   EXPORTS
===================================================== */

export const {
  addLiveUpdate,
  updateEmployeeStatus,
  setSocketConnected,
  setDepartmentFilter,
  setStatusFilter,
  setSearchFilter,
  clearLiveUpdates
} = attendanceDashboardSlice.actions;

export default attendanceDashboardSlice.reducer;